import type { VNode } from "preact";
import { useCallback, useState } from "preact/hooks";

/** Toasts carrying an action (e.g. “Undo” after a destructive settings
 *  change) stay a little longer so there is time to click it. */
export const ACTION_TOAST_MS = 7000;

export type Toast = {
  id: string;
  kind: "success" | "error" | "info";
  text: string;
  /** Optional inline action button, e.g. Undo after a reset. When clicked,
   *  the action runs and the toast dismisses itself. */
  action?: { label: string; onClick: () => void };
};

/** Shared toast state — used by the dashboard and by standalone pages
 *  (e.g. /settings) so transient feedback behaves identically everywhere. */
export function useToasts(lifetimeMs = 3500) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback(
    (t: Omit<Toast, "id">) => {
      const id = Math.random().toString(36).slice(2);
      const lifetime = t.action
        ? Math.max(lifetimeMs, ACTION_TOAST_MS)
        : lifetimeMs;
      // Wrap the action so clicking it also dismisses this toast.
      const action = t.action
        ? {
            label: t.action.label,
            onClick: () => {
              t.action!.onClick();
              setToasts((cur) => cur.filter((x) => x.id !== id));
            },
          }
        : undefined;
      setToasts((cur) => [...cur, { ...t, id, action }]);
      setTimeout(() => {
        setToasts((cur) => cur.filter((x) => x.id !== id));
      }, lifetime);
    },
    [lifetimeMs],
  );
  return { toasts, pushToast };
}

export function ToastHost({ toasts }: { toasts: Toast[] }): VNode {
  return (
    <div class="toast-host">
      {toasts.map((t) => (
        <div class={`toast toast--${t.kind}`} key={t.id}>
          <span class="toast__text" dangerouslySetInnerHTML={{ __html: t.text}}/>
          {t.action && (
            <button
              class="btn btn--ghost btn--sm toast__action"
              onClick={t.action.onClick}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
